import { useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Alert, Button, Card, Dialog, Link, Skeleton, Text } from '@/ui'
import { mtc, TypographyStyles } from '@/styles'
import { centsToCurrency } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFetchError, selectIsFetchingForKeys } from '@/reducers/fetch'
import type {
  RankedTierOffer,
  RecommendedOffersFetchResult,
} from '@/features/compliance/plan-pricing/recommendation.types'
import { usePlanChangeAnalytics } from './analytics/use-plan-change-analytics'
import { CadenceToggle } from './CadenceToggle'
import { ComparisonTable } from './ComparisonTable'
import { SUPPORT_EMAIL, SupportLink } from './SupportLink'
import { CurrentPlanCard } from './CurrentPlanCard'
import { CARD_RADIUS, PLAN_CARD_SHELL } from './PlanCardParts'
import { PendingChangeBanner } from './PendingChangeBanner'
import { PlanOptionCard } from './PlanOptionCard'
import { displayRowKeysFor } from './lib/taxFilingRows'
import { audienceEyebrowsFor } from './lib/tierDisplayNames'
import { PAYROLL_PERK_KEPT } from './lib/withheldReasons'
import { selectIsPayrollActive } from '@/features/payroll/payroll.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'
import { planTierServicesFor } from '@/constants/productFeatureConstants'
import {
  selectIsCurrentUserScorp,
  selectIsGroupPractice,
} from '@/selectors/user.selectors'
import {
  BILLING_LINE,
  BILLING_TABS,
  CADENCE_SUFFIX,
  PLAN_CHANGE_STEP,
  TIER_DISPLAY_NAMES,
  planChangeCardTitle,
  useBaseTierDisplayNames,
  useCatalogTierDisplayNames,
  useTierDisplayNames,
  type TierDisplayNames,
  annualSavingsHintCopy,
  bestAnnualSavingCents,
  defaultCadenceFor,
  formatPlanDate,
  pendingChangeFinalCopy,
  unofferedPendingTier,
  usePlanChangeNavigation,
} from './lib/helpers'
import {
  CANCEL_PENDING_PLAN_CHANGE_KEY,
  FETCH_PLAN_OPTIONS_KEY,
  cancelPendingPlanChange,
  fetchPlanCatalog,
  fetchPlanOptions,
} from './lib/planChange.slice'
import {
  selectHasCatalogRequestFailed,
  selectPlanCatalog,
  selectPlanOptions,
} from './lib/planChange.selectors'
import type {
  CurrentPlan,
  ExternalScheduledChange,
  PlanCadence,
} from './lib/planChange.types'

const PlanChangeSkeleton = () => (
  <div className="flex flex-col gap-6" data-testid="plan-change-skeleton">
    <Skeleton className="h-9 w-[260px]" />
    <Skeleton className="h-10 w-[300px]" />
    <div className="grid grid-cols-[repeat(auto-fill,minmax(272px,352px))] items-stretch gap-4 [&>*]:min-w-0">
      {[0, 1, 2].map((slot) => (
        <Skeleton
          key={slot}
          className={mtc(PLAN_CARD_SHELL, 'h-[420px] border-transparent')}
        />
      ))}
    </div>
    <Skeleton className={mtc('h-[320px] w-full', CARD_RADIUS)} />
  </div>
)

const ComparisonSkeleton = () => (
  <Skeleton
    data-testid="plan-comparison-skeleton"
    className="h-[320px] w-full"
  />
)

const LoadErrorState = ({
  title,
  onRetry,
}: {
  title: string
  onRetry?: () => void
}) => (
  <Card className="flex flex-col items-start gap-4">
    <Text variant={TypographyStyles.DISPLAY_SM}>{title}</Text>
    <Text variant={TypographyStyles.BODY_MD}>
      Try again in a moment. If it keeps happening, reach out to <SupportLink />{' '}
      and we&rsquo;ll sort it out.
    </Text>
    {onRetry && <Button onClick={onRetry}>Try again</Button>}
  </Card>
)

const OptionsErrorState = ({ onRetry }: { onRetry?: () => void }) => (
  <LoadErrorState
    title="We couldn&rsquo;t load your plan options"
    onRetry={onRetry}
  />
)

const ComparisonFallback = ({
  hasFailed,
  onRetry,
}: {
  hasFailed: boolean
  onRetry: () => void
}) =>
  hasFailed ? (
    <LoadErrorState
      title="We couldn&rsquo;t load the plan comparison"
      onRetry={onRetry}
    />
  ) : (
    <ComparisonSkeleton />
  )

/** The refusal states are still the plan page, so they carry its heading and its width. */
const PlanChangeNotice = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) => (
  <div className="flex flex-col gap-6">
    <Text variant={TypographyStyles.H1}>Change your plan</Text>
    <Card
      className={mtc('max-w-[560px]', CARD_RADIUS)}
      childrenClassName="flex flex-col items-start gap-4"
    >
      <Text variant={TypographyStyles.DISPLAY_SM}>{title}</Text>
      {children}
    </Card>
  </div>
)

const CurrentPlanSummary = ({ currentPlan }: { currentPlan: CurrentPlan }) => {
  const names = useTierDisplayNames()
  return (
    <Text variant={TypographyStyles.BODY_MD}>
      You&rsquo;re on {names[currentPlan.tier]} at{' '}
      {centsToCurrency(currentPlan.effectiveAmountCents).format()}
      {CADENCE_SUFFIX[currentPlan.cadence]}.
    </Text>
  )
}

// A cancelling subscription bills no further, so the rate is the one fact that no longer
// applies. The date access ends is what the customer needs.
const CancellingPlanSummary = ({
  currentPlan,
}: {
  currentPlan: CurrentPlan
}) => {
  const names = useTierDisplayNames()
  return (
    <Text variant={TypographyStyles.BODY_MD}>
      You&rsquo;re on {names[currentPlan.tier]} until{' '}
      {formatPlanDate(currentPlan.currentPeriodEnd)}.
    </Text>
  )
}

const externalChangeSentence = (
  change: ExternalScheduledChange,
  names: TierDisplayNames
): string => {
  const date = formatPlanDate(change.effectiveAt)
  if (change.kind === 'cancel') {
    return `Your plan is scheduled to cancel on ${date}.`
  }
  return `Your plan is scheduled to change to ${names[change.toTier]}, ${BILLING_LINE[change.toCadence].toLowerCase()}, on ${date}.`
}

const PAYROLL: string = ProductFeatureId.payroll
const FREE_PAYROLL: string = ProductFeatureId.payrollIncludedForFreeForSelf
const GROUP_PRACTICE_SUPPORT: string = ProductFeatureId.groupPracticeSupport
const FOUR_UP_TILE_COUNT = 4
const SINGLE_TILE_COUNT = 1
const CARD_GRID_FOUR_UP =
  'grid grid-cols-1 items-stretch gap-4 @[560px]:grid-cols-2 @[1000px]:grid-cols-4 [&>*]:min-w-0 [&>*]:max-w-none!'
const CARD_ROW_FILL =
  'flex flex-wrap items-stretch gap-4 [&>*]:min-w-0 [&>*]:max-w-none! [&>*]:grow [&>*]:basis-full @[560px]:[&>*]:basis-[calc(50%_-_8.5px)] @[820px]:[&>*]:basis-[calc(33.333%_-_11.2px)]'
const SINGLE_TILE_WIDTH_CAP = 'max-w-[440px]'
const CHANGE_SOMETHING_ELSE_COPY = 'Need to change something else?'
const TALK_TO_US_COPY = 'Talk to us'

const gridClassFor = (tileCount: number): string => {
  if (tileCount === FOUR_UP_TILE_COUNT) return CARD_GRID_FOUR_UP
  if (tileCount === SINGLE_TILE_COUNT) {
    return mtc(CARD_ROW_FILL, SINGLE_TILE_WIDTH_CAP)
  }
  return CARD_ROW_FILL
}

export const ChoosePlanStep = ({
  recommendation,
}: {
  recommendation: RecommendedOffersFetchResult | null
}) => {
  const tierNames = useTierDisplayNames()
  const baseNames = useBaseTierDisplayNames()
  const catalogNames = useCatalogTierDisplayNames()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { goToStep } = usePlanChangeNavigation()
  const { trackTargetSelected, trackPendingCancelled } =
    usePlanChangeAnalytics()
  const planOptions = useReselector(selectPlanOptions)
  const planCatalog = useReselector(selectPlanCatalog)
  const isFetchingOptions = useReselector(selectIsFetchingForKeys, [
    FETCH_PLAN_OPTIONS_KEY,
  ])
  const hasCatalogFailed = useReselector(selectHasCatalogRequestFailed)
  const optionsError = useReselector(getFetchError, FETCH_PLAN_OPTIONS_KEY)
  const isPayrollActive = useReselector(selectIsPayrollActive)
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const isGroupPractice = useReselector(selectIsGroupPractice)
  const cancelError = useReselector(
    getFetchError,
    CANCEL_PENDING_PLAN_CHANGE_KEY
  )
  const [chosenCadence, setChosenCadence] = useState<PlanCadence | null>(null)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const conflictState: unknown = location.state?.conflict

  const refetchOptions = () => {
    fetchPlanOptions()(dispatch)
  }

  const refetchCatalog = () => {
    dispatch(fetchPlanCatalog())
  }

  const openCancelConfirm = (): Promise<void> => {
    setCancelConfirmOpen(true)
    return Promise.resolve()
  }

  const confirmCancelPendingChange = async () => {
    setCancelConfirmOpen(false)
    const cancelled = await cancelPendingPlanChange()(dispatch)
    if (cancelled) {
      if (planOptions?.currentPlan) {
        trackPendingCancelled({
          fromTier: planOptions.currentPlan.tier,
          fromCadence: planOptions.currentPlan.cadence,
        })
      }
      await fetchPlanOptions()(dispatch)
    }
  }

  if (optionsError) {
    if (optionsError.statusCode === 404)
      return <Navigate replace to="/accounts" />
    return <OptionsErrorState onRetry={refetchOptions} />
  }

  if (!planOptions) {
    return isFetchingOptions ? (
      <PlanChangeSkeleton />
    ) : (
      <OptionsErrorState onRetry={refetchOptions} />
    )
  }

  const {
    currentPlan,
    targets,
    pendingChange,
    suppressedTargetCount,
    couponScopeUnreadable,
    unavailableReason,
    externalScheduledChange,
    customerType,
  } = planOptions
  const finalPendingTier = unofferedPendingTier(
    pendingChange,
    targets,
    suppressedTargetCount
  )

  if (unavailableReason) {
    switch (unavailableReason) {
      case 'no_subscription':
        return <Navigate replace to="/accounts" />
      case 'trialing':
        return <Navigate replace to="/accounts/choose-plan" />
      case 'cancelling':
        return (
          <PlanChangeNotice title="Plan changes are paused">
            {currentPlan && <CancellingPlanSummary currentPlan={currentPlan} />}
            <Text variant={TypographyStyles.BODY_MD}>
              Because your plan is set to cancel, you can&rsquo;t change plans
              right now. Reactivate first and your options come back.
            </Text>
            <Link href="/accounts">Go to billing settings</Link>
          </PlanChangeNotice>
        )
      case 'externally_scheduled':
        return (
          <PlanChangeNotice title="Plan changes are paused">
            {currentPlan && <CurrentPlanSummary currentPlan={currentPlan} />}
            <Text variant={TypographyStyles.BODY_MD}>
              {externalScheduledChange ? (
                <>
                  {externalChangeSentence(
                    externalScheduledChange,
                    catalogNames
                  )}{' '}
                  To change this, email <SupportLink /> and we&rsquo;ll pick it
                  up from there.
                </>
              ) : (
                <>
                  A change to your plan is being handled by our support team.
                  Email <SupportLink /> and we&rsquo;ll pick it up from there.
                </>
              )}
            </Text>
          </PlanChangeNotice>
        )
      case 'inactive':
        return (
          <PlanChangeNotice title="Plan changes are paused">
            {currentPlan && <CurrentPlanSummary currentPlan={currentPlan} />}
            <Text variant={TypographyStyles.BODY_MD}>
              Your subscription isn&rsquo;t active right now, so it can&rsquo;t
              be changed. Email <SupportLink /> and we&rsquo;ll get it running
              again.
            </Text>
          </PlanChangeNotice>
        )
      case 'fixed_discount':
        return (
          <PlanChangeNotice title="Changing plans needs a hand from us">
            {currentPlan && <CurrentPlanSummary currentPlan={currentPlan} />}
            <Text variant={TypographyStyles.BODY_MD}>
              {currentPlan?.discountLabel
                ? `Your ${currentPlan.discountLabel} is a fixed amount, so it doesn't carry over to a new plan's price cleanly. Email `
                : "Your plan has a custom discount that doesn't carry over to a new plan's price cleanly. Email "}
              <SupportLink /> and we&rsquo;ll go through your options with you.
            </Text>
          </PlanChangeNotice>
        )
      case 'unresolvable_plan':
        return <OptionsErrorState />
      default:
        return <OptionsErrorState />
    }
  }

  if (!currentPlan) return <OptionsErrorState onRetry={refetchOptions} />

  const activeCadence = chosenCadence ?? defaultCadenceFor(currentPlan)

  const visibleTargets = targets.filter(
    (target) => target.cadence === activeCadence
  )

  // Every target is a catalog product, so only the current plan can be legacy.
  const tierServicesFor = (tier: string, isCurrentPlan: boolean): string[] =>
    planTierServicesFor({
      tier,
      isLegacy: isCurrentPlan && currentPlan.onCatalogProduct === false,
      isScorp,
      isGroupPractice,
    })

  const sCorpServiceFor = (
    tier: string,
    isCurrentPlan: boolean
  ): string | null =>
    tierServicesFor(tier, isCurrentPlan).find(
      (service) => service !== GROUP_PRACTICE_SUPPORT
    ) ?? null

  // Payroll access comes from the payroll subscription, not the plan, and a banked signup
  // coupon survives a plan change, so dropping the grant costs nothing worth stating.
  const payrollSaysSomething =
    isGroupPractice ||
    (!isPayrollActive && !(currentPlan.featureIds ?? []).includes(PAYROLL))
  const hideRows = [
    ...(payrollSaysSomething ? [] : [PAYROLL]),
    ...(targets.some((target) =>
      target.withheld.some(
        ({ featureId, reason }) =>
          featureId === FREE_PAYROLL && PAYROLL_PERK_KEPT.includes(reason)
      )
    )
      ? [FREE_PAYROLL]
      : []),
  ]

  const offersByTier = new Map<string, RankedTierOffer>(
    recommendation?.status === 'ok'
      ? recommendation.offers.map((offer) => [offer.tier, offer])
      : []
  )
  const currentOffer = offersByTier.get(currentPlan.tier)
  const recommendedLookupKeys = new Set(
    visibleTargets
      .filter((target) => target.recommended)
      .map((target) => target.lookupKey)
  )
  const hasRecommendation = recommendedLookupKeys.size > 0
  const ownFeatures = currentPlan.featureIds ?? currentOffer?.featureIds ?? null
  const ownFeatureIds = ownFeatures
    ? [
        ...displayRowKeysFor(ownFeatures),
        ...tierServicesFor(currentPlan.tier, true),
      ].filter((key) => !hideRows.includes(key))
    : null

  const audienceEyebrows = audienceEyebrowsFor(
    [currentPlan.tier, ...visibleTargets.map((target) => target.tier)],
    customerType
  )

  const bestAnnualSaving = bestAnnualSavingCents(currentPlan, targets)
  const savingsHint =
    bestAnnualSaving === null ? null : annualSavingsHintCopy(bestAnnualSaving)

  // The slice clears the catalog when new options arrive, so a stored catalog is never stale.
  const comparisonSection = planCatalog ? (
    <ComparisonTable
      hideRows={hideRows}
      sCorpServiceFor={sCorpServiceFor}
      catalog={planCatalog}
      currentPlan={currentPlan}
      targets={targets}
      cadence={activeCadence}
      recommendedLookupKeys={recommendedLookupKeys}
    />
  ) : (
    <ComparisonFallback hasFailed={hasCatalogFailed} onRetry={refetchCatalog} />
  )

  const cancelErrorMessage = cancelError
    ? cancelError.statusCode === 403
      ? "Cancelling a scheduled plan change isn't available while impersonating a customer."
      : `${cancelError.message} Try again in a moment.`
    : null

  return (
    <div className="flex flex-col gap-6">
      <Text variant={TypographyStyles.H1}>Change your plan</Text>

      {pendingChange && (
        <PendingChangeBanner
          pendingChange={pendingChange}
          onCancel={openCancelConfirm}
          cancelError={cancelErrorMessage}
        />
      )}

      {conflictState === true && (
        <Alert variant="warning">
          Something changed with your plan options. Pick again.
        </Alert>
      )}

      {suppressedTargetCount > 0 && (
        <Alert variant="warning">
          <div className="flex flex-col items-start gap-2">
            <Text variant={TypographyStyles.BODY_MD}>
              We couldn&rsquo;t load {suppressedTargetCount} of your plan
              options.
            </Text>
            <Button variant="secondary" onClick={refetchOptions}>
              Try again
            </Button>
          </div>
        </Alert>
      )}

      {couponScopeUnreadable && (
        <Alert variant="warning">
          We couldn&rsquo;t check your discount right now, so you can&rsquo;t
          change plans yet. Try again later, or email <SupportLink /> if this
          keeps happening.
        </Alert>
      )}

      {targets.length === 0 ? (
        <>
          <Card
            className={mtc('max-w-[560px]', CARD_RADIUS)}
            childrenClassName="flex flex-col items-start gap-4"
          >
            <Text variant={TypographyStyles.DISPLAY_SM}>
              No plan changes available
            </Text>
            <CurrentPlanSummary currentPlan={currentPlan} />
            <Text variant={TypographyStyles.BODY_MD}>
              There&rsquo;s nothing to switch to from your current plan. Email{' '}
              <SupportLink /> if you expected something here.
            </Text>
          </Card>

          {comparisonSection}
        </>
      ) : (
        <>
          <CadenceToggle
            cadences={BILLING_TABS}
            selected={activeCadence}
            onSelect={setChosenCadence}
            savingsHint={savingsHint}
          />

          <div className="@container">
            <div className={gridClassFor(visibleTargets.length + 1)}>
              <CurrentPlanCard
                currentPlan={currentPlan}
                displayName={planChangeCardTitle(
                  currentPlan.tier,
                  currentOffer?.displayName,
                  baseNames
                )}
                eyebrow={audienceEyebrows[currentPlan.tier] ?? null}
                includedFeatureIds={ownFeatureIds}
                hasPendingChange={Boolean(pendingChange)}
                reserveBadgeRow={hasRecommendation}
              />

              {visibleTargets.map((target) => {
                const offer = offersByTier.get(target.tier)
                const { recommended: isRecommended } = target
                return (
                  <PlanOptionCard
                    key={target.lookupKey}
                    target={target}
                    currentPlan={currentPlan}
                    displayName={planChangeCardTitle(
                      target.tier,
                      offer?.displayName,
                      catalogNames
                    )}
                    title={planChangeCardTitle(
                      target.tier,
                      offer?.displayName,
                      TIER_DISPLAY_NAMES
                    )}
                    eyebrow={audienceEyebrows[target.tier] ?? null}
                    includedFeatureIds={
                      offer
                        ? [
                            ...displayRowKeysFor(offer.featureIds),
                            ...tierServicesFor(target.tier, false),
                          ].filter((key) => !hideRows.includes(key))
                        : null
                    }
                    isRecommended={isRecommended}
                    hasRecommendation={hasRecommendation}
                    disabled={couponScopeUnreadable}
                    onSelect={(chosen) => {
                      trackTargetSelected({
                        fromTier: currentPlan.tier,
                        fromCadence: currentPlan.cadence,
                        toTier: chosen.tier,
                        toCadence: chosen.cadence,
                        isRecommended,
                      })
                      goToStep(PLAN_CHANGE_STEP.confirm, {
                        lookupKey: chosen.lookupKey,
                        isRecommended,
                      })
                    }}
                  />
                )
              })}
            </div>
          </div>

          {comparisonSection}

          <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
            {CHANGE_SOMETHING_ELSE_COPY}{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}`} newPage>
              {TALK_TO_US_COPY}
            </Link>
          </Text>
        </>
      )}

      <Dialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <Dialog.Content>
          <Dialog.Title>Cancel this scheduled plan change?</Dialog.Title>
          <Text variant={TypographyStyles.BODY_LG}>
            {pendingChange
              ? `You'll stay on ${tierNames[currentPlan.tier]} and nothing will change on ${formatPlanDate(pendingChange.effectiveAt)}.${finalPendingTier ? ` ${pendingChangeFinalCopy(finalPendingTier, catalogNames)}` : ''}`
              : ''}
          </Text>
          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button variant="secondary">Keep the change</Button>
            </Dialog.Close>
            <Button variant="primary" onClick={confirmCancelPendingChange}>
              Yes, cancel change
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </div>
  )
}
